import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ApiService } from "../services/api";
import {
  X,
  GripVertical,
  Lock,
  CreditCard,
} from "lucide-react";
import checkPermission, { checkSuperAdmin } from "../utils/permission";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { defaultMenuItems } from "./data/defaultMenuItems";
import { isYesToolsHostname } from "../utils/yesTools";

/** Matches sidebar item path to current URL (nested routes count as inside the section). */
function isSidebarRouteActive(pathname: string, itemPath: string): boolean {
  const route = itemPath || "/dashboard";
  const norm =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  if (route === "/dashboard") {
    return norm === "/dashboard" || norm === "/" || norm.startsWith("/dashboard/");
  }

  return norm === route || norm.startsWith(`${route}/`);
}

const Sidebar = ({
  isExpanded,
  toggleSidebar,
  additionalApps = [],
  onRemoveApp,
  isEditMode,
  onSortChange,
}: {
  isExpanded: boolean;
  toggleSidebar: () => void;
  additionalApps: any[];
  onRemoveApp: (appId: string) => void;
  isEditMode: boolean;
  onSortChange: (newSort: string[]) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme } = useTheme();
  const { notifications, fileUsage, user } = useAuth();
  const canListFiles = checkPermission("file_list");
  const [isMenuOpen, setMenuOpen] = useState(true);
  const [hoveredApp, setHoveredApp] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [mobileWidth, setMobileWidth] = useState(220);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [collapsedFlyout, setCollapsedFlyout] = useState<{ label: string; top: number; left: number } | null>(null);
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);

  const getInitialSidebarSort = () => {
    const defaultSort = [
      "home",
      "announcements",
      "surveys",
      "employees",
      "meetings",
      "calendar",
      "lessons",
      "planning",
      "appointments",
      "todos",
      "tasks",
      "files",
      "practice-exams",
      "maarif-test-generator",
      "maarif-question-wizard",
      "question-bank-indesign",
      "social",
      "whiteboard",
      "speed-reading",
    ];

    try {
      const stored = localStorage.getItem("sidebarSort");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const allModuleIds = defaultMenuItems.map((item) => item.id);
          const missingModules = allModuleIds.filter((id) => !parsed.includes(id));

          return [...parsed, ...missingModules];
        }
      }
    } catch (error) {
      console.error("Error parsing sidebarSort from localStorage:", error);
    }
    return defaultSort;
  };

  const [sidebarSort, setSidebarSort] = useState<string[]>(getInitialSidebarSort());
  const [localSortedItems, setLocalSortedItems] = useState<string[]>([]);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768;
      setIsMobile(isMobileScreen);

      if (isMobileScreen) {
        const calculatedWidth = Math.max(200, Math.min(220, window.innerWidth * 0.65));
        setMobileWidth(calculatedWidth);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isExpanded || isMobile) {
      setCollapsedFlyout(null);
    }
  }, [isExpanded, isMobile]);

  useEffect(() => {
    const handleSidebarSortLoaded = () => {
      const newSort = getInitialSidebarSort();
      setSidebarSort(newSort);
    };

    const handleSidebarSortUpdated = () => {
      const newSort = getInitialSidebarSort();
      setSidebarSort(newSort);
    };

    window.addEventListener("sidebarSortLoaded", handleSidebarSortLoaded);
    window.addEventListener("sidebarSortUpdated", handleSidebarSortUpdated);

    return () => {
      window.removeEventListener("sidebarSortLoaded", handleSidebarSortLoaded);
      window.removeEventListener("sidebarSortUpdated", handleSidebarSortUpdated);
    };
  }, []);

  useEffect(() => {
    const handleUserLoggedOut = () => {
      const defaultSort = defaultMenuItems.map((item) => item.id);
      setSidebarSort(defaultSort);
    };

    window.addEventListener("userLoggedOut", handleUserLoggedOut);

    return () => {
      window.removeEventListener("userLoggedOut", handleUserLoggedOut);
    };
  }, []);

  useEffect(() => {
    if (sidebarSort && sidebarSort.length > 0 && localSortedItems.length === 0) {
      setLocalSortedItems(sidebarSort);
    }
  }, [sidebarSort, localSortedItems.length]);

  const currentSortedItems = isEditMode ? localSortedItems : sidebarSort;

  const toggleMenu = () => {
    if (isExpanded) {
      setMenuOpen((prev) => !prev);
    }
  };

  const removeApp = (appId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemoveApp) {
      onRemoveApp(appId);
    }
  };

  const allMenuItems = [...defaultMenuItems, ...additionalApps];
  const paymentAccessMap = new Map(
    (user?.paymentAccess?.modules || []).map((module: any) => [module.id, module.access])
  );
  // Kilitli bölümün altında "X ile açılır" yazısı: o bölümü açan paket (birden çok paket açıyorsa genel ifade).
  const paymentUnlockMap = new Map<string, string>(
    (user?.paymentAccess?.modules || []).map((module: any) => [
      module.id,
      module.unlockPackageCount === 1 && module.unlockPackageLabel ? `${module.unlockPackageLabel} ile açılır` : "Paketle açılır",
    ])
  );

  const canShowItem = (item: any) => {
    const access = paymentAccessMap.get(item.id);
    // TRIAL = deneme sürüyor → modüle girişe izin (içeride sayaç işler, süre dolunca paywall)
    if (access === "AUTHORIZED" || access === "PAYMENT_REQUIRED" || access === "TRIAL") return true;
    if (access === "HIDDEN") return false;
    return item.permission === undefined || checkSuperAdmin() || checkPermission(item.permission);
  };

  const getSortedMenuItems = () => {
    const itemsToUse = currentSortedItems && currentSortedItems.length > 0 ? currentSortedItems : sidebarSort;
    const visibleItems = allMenuItems
      .filter((item: any) => !item.yesToolsOnly || isYesToolsDomain)
      .filter(canShowItem)
      .map((item: any) => {
        // Koçum menüde kilitlenmez: Kitaplarım ücretsizdir, plan ve hedef sayfanın içinde kilitli kalır.
        const paymentModuleId = item.id;
        const paymentRequired = paymentAccessMap.get(paymentModuleId) === "PAYMENT_REQUIRED";
        return {
          ...item,
          paymentModuleId,
          paymentRequired,
          paymentHint: paymentRequired ? paymentUnlockMap.get(paymentModuleId) : undefined,
        };
      });

    if (!itemsToUse || itemsToUse.length === 0) {
      return visibleItems;
    }

    const itemsMap = new Map(visibleItems.map((item: any) => [item.id, item]));
    const sorted: any[] = [];

    itemsToUse.forEach((id: string) => {
      const item = itemsMap.get(id);
      if (item) {
        sorted.push(item);
        itemsMap.delete(id);
      }
    });

    itemsMap.forEach((item: any) => {
      sorted.push(item);
    });

    return sorted;
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    if (!isEditMode) return;
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", itemId);
  };

  const handleDragOver = (e: React.DragEvent, targetItemId: string) => {
    if (!isEditMode || !draggedItemId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedItemId === targetItemId) return;

    const itemsToUpdate = localSortedItems.length > 0 ? localSortedItems : sidebarSort;
    const newSortedItems = [...itemsToUpdate];
    const draggedIndex = newSortedItems.indexOf(draggedItemId);
    const targetIndex = newSortedItems.indexOf(targetItemId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newSortedItems.splice(draggedIndex, 1);
      newSortedItems.splice(targetIndex, 0, draggedItemId);
      setLocalSortedItems(newSortedItems);

      if (onSortChange) {
        onSortChange(newSortedItems);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  useEffect(() => {
    if (!isEditMode) {
      setLocalSortedItems([]);
    } else if (isEditMode && localSortedItems.length === 0) {
      setLocalSortedItems(sidebarSort);
    }
  }, [isEditMode, sidebarSort]);

  const formatFileSize = (kb: number) => {
    if (kb === 0) return "0 MB";
    const mb = kb / 1024;
    return mb.toFixed(2) + " MB";
  };

  return (
    <>
      {isMobile && isExpanded && <div className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden" onClick={toggleSidebar} />}

      <div
        className={`
          fixed left-0 top-[60px] h-[calc(100vh-60px)] z-20 transition-all duration-300 overflow-hidden flex flex-col
          ${isMobile ? "shadow-2xl backdrop-blur-md border-r border-white/10" : "bg-transparent"}
        `}
        style={{
          ...(isMobile ? { backgroundColor: `${currentTheme.colors.background.base}f2` } : {}),
          // dvh = GÖRÜNÜR yükseklik. iOS Safari'de 100vh adres/araç çubuğu gizliymiş gibi
          // ölçer; menünün altı ekranın dışında kalıp son öğe (Ödeme/Paketim) hiç
          // görünmüyordu. dvh bilmeyen tarayıcı bu satırı yok sayar, class'taki 100vh kalır.
          height: "calc(100dvh - 60px)",
          width: isExpanded ? (isMobile ? mobileWidth : 240) : isMobile && isExpanded ? mobileWidth : 70,
          transform: isMobile && !isExpanded ? "translateX(-100%)" : "translateX(0)",
        }}>
        <div
          className={`h-12 shrink-0 flex items-center px-4 cursor-pointer touch-manipulation relative ${isExpanded ? "" : "cursor-default"}`}
          onClick={toggleMenu}>
          {isExpanded && (
            <div className="text-white text-sm font-medium flex items-center justify-between w-full">
              <span className="select-none">Tüm Uygulamalar</span>
              {!isMobile && <span className={`ml-2 text-white transition-transform select-none ${isMenuOpen ? "rotate-180" : ""}`}>▼</span>}
            </div>
          )}

          {isMobile && isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
              className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-white hover:bg-gray-700 rounded transition-colors touch-manipulation">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Yükseklikler artık esnek: sabit calc(100vh-192px) "başlık 60 + 48 + depolama
            kutusu 84" varsayıyordu; kutu yokken boşluk, iOS'ta ise liste ekran dışına
            taşıyordu. flex-1 + min-h-0 gerçek kalan alanı kullanır. */}
        <div
          className={`overflow-hidden transition-all duration-300 ${isMenuOpen ? "flex-1 min-h-0 flex flex-col" : "h-0 shrink-0"}`}>
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-2"
            style={{
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              // Depolama kutusu her zaman render edilmiyor; son öğe (Ödeme/Paketim)
              // iPhone'un ana ekran çubuğunun altında kalmasın.
              paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
            }}
            onScroll={() => {
              if (!isExpanded && !isMobile) setCollapsedFlyout(null);
            }}>
            {getSortedMenuItems().map((item: any) => {
              const routeActive =
                !isEditMode && isSidebarRouteActive(location.pathname, item.path);

              const rowInner = (
                  <div
                    className={`flex items-center px-3 py-3 sm:py-2 text-white cursor-pointer transition-colors duration-200 group touch-manipulation mx-0.5 rounded-md ${
                      routeActive ? "bg-white/10" : ""
                    } ${
                      !isEditMode
                        ? routeActive
                          ? "hover:bg-white/[0.14]"
                          : "hover:bg-white/[0.07]"
                        : ""
                    }`}>
                    {isEditMode && isExpanded && (
                      <div className="w-5 h-5 flex items-center justify-center mr-1 text-gray-400 cursor-grab active:cursor-grabbing">
                        <GripVertical size={16} />
                      </div>
                    )}
                    <div className="relative">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded text-white flex-shrink-0"
                        style={{ backgroundColor: item.color }}>
                        <item.icon size={16} />
                        {item.paymentRequired && (
                          <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-amber-950 shadow">
                            <Lock size={9} />
                          </span>
                        )}
                      </div>
                      {!isExpanded && item.id === "appointments" && notifications?.appointments?.count > 0 && (
                        <div
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: currentTheme.colors.primary, fontSize: "10px" }}>
                          {notifications.appointments.count > 9 ? "9+" : notifications.appointments.count}
                        </div>
                      )}
                      {!isExpanded && item.id === "whiteboard" && notifications?.whiteboard?.count > 0 && (
                        <div
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: currentTheme.colors.primary, fontSize: "10px" }}>
                          {notifications.whiteboard.count > 9 ? "9+" : notifications.whiteboard.count}
                        </div>
                      )}
                      {!isExpanded && item.id === "tasks" && notifications?.tasks?.count > 0 && (
                        <div
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: currentTheme.colors.primary, fontSize: "10px" }}>
                          {notifications.tasks.count > 9 ? "9+" : notifications.tasks.count}
                        </div>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="ml-3 flex min-w-0 flex-1 items-center justify-between">
                        <span className="min-w-0 select-none">
                          <span className="block text-[0.775rem] font-semibold text-white">
                            {item.label}
                          </span>
                          {item.paymentRequired && (
                            <span className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-amber-200">
                              <Lock size={9} className="shrink-0" />
                              <span className="truncate">{item.paymentHint}</span>
                            </span>
                          )}
                        </span>
                        {item.id === "appointments" && notifications?.appointments?.count > 0 && (
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ml-2"
                            style={{ backgroundColor: currentTheme.colors.primary }}>
                            {notifications.appointments.count}
                          </div>
                        )}
                        {item.id === "whiteboard" && notifications?.whiteboard?.count > 0 && (
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ml-2"
                            style={{ backgroundColor: currentTheme.colors.primary }}>
                            {notifications.whiteboard.count}
                          </div>
                        )}
                        {item.id === "tasks" && notifications?.tasks?.count > 0 && (
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ml-2"
                            style={{ backgroundColor: currentTheme.colors.primary }}>
                            {notifications.tasks.count}
                          </div>
                        )}
                      </div>
                    )}

                    {isExpanded &&
                      !isEditMode &&
                      additionalApps.some((app) => app.id === item.id) &&
                      (hoveredApp === item.id || isMobile) && (
                        <button
                          onClick={(e) => removeApp(item.id, e)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors ml-2 touch-manipulation">
                          <X size={14} />
                        </button>
                      )}
                  </div>
              );

              return (
              <div
                key={item.id}
                className="relative"
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, item.id)}
                onDragOver={(e) => handleDragOver(e, item.id)}
                onDragEnd={handleDragEnd}
                onMouseEnter={(e) => {
                  if (!isMobile) setHoveredApp(item.id);
                  if (!isExpanded && !isMobile && !isEditMode) {
                    const r = e.currentTarget.getBoundingClientRect();
                    setCollapsedFlyout({
                      label: item.label,
                      top: r.top + r.height / 2,
                      left: r.right + 8,
                    });
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile) setHoveredApp(null);
                  if (!isExpanded && !isMobile) setCollapsedFlyout(null);
                }}
                onTouchStart={() => isMobile && setHoveredApp(item.id)}
                style={{
                  opacity: isEditMode && draggedItemId === item.id ? 0.5 : 1,
                }}>
                {isEditMode ? (
                  <div className="block">{rowInner}</div>
                ) : (
                  <Link
                    to={item.paymentRequired ? `/payment?module=${encodeURIComponent(item.paymentModuleId)}&returnTo=${encodeURIComponent(item.path)}` : item.path}
                    className="block rounded-md focus:outline-none focus-visible:ring-1 focus-visible:ring-white/35"
                    aria-label={!isExpanded && !isMobile ? item.label : undefined}
                    aria-current={routeActive ? "page" : undefined}
                    onClick={() => {
                      if (isMobile) toggleSidebar();
                    }}>
                    {rowInner}
                  </Link>
                )}
              </div>
              );
            })}

            {/* Ödeme / Paketim — ücretli departmandaki kullanıcıya (öğrenci) özel; proaktif ödeme/yenileme girişi */}
            {user?.paymentAccess?.paymentApplicable && (() => {
              const paidActive = Boolean(user?.paymentAccess?.paidActive);
              const label = paidActive ? "Paketim" : "Ödeme";
              const active = isSidebarRouteActive(location.pathname, "/payment");
              return (
                <div className="relative">
                  <Link
                    to="/payment"
                    className="block rounded-md focus:outline-none focus-visible:ring-1 focus-visible:ring-white/35"
                    aria-label={!isExpanded && !isMobile ? label : undefined}
                    aria-current={active ? "page" : undefined}
                    onClick={() => { if (isMobile) toggleSidebar(); }}
                    onMouseEnter={(e) => {
                      if (!isExpanded && !isMobile) {
                        const r = e.currentTarget.getBoundingClientRect();
                        setCollapsedFlyout({ label, top: r.top + r.height / 2, left: r.right + 8 });
                      }
                    }}
                    onMouseLeave={() => { if (!isExpanded && !isMobile) setCollapsedFlyout(null); }}>
                    <div className={`flex items-center px-3 py-3 sm:py-2 text-white cursor-pointer transition-colors duration-200 group touch-manipulation mx-0.5 rounded-md ${active ? "bg-white/10 hover:bg-white/[0.14]" : "hover:bg-white/[0.07]"}`}>
                      <div className="w-8 h-8 flex items-center justify-center rounded text-white flex-shrink-0" style={{ backgroundColor: "#7c3aed" }}>
                        <CreditCard size={16} />
                      </div>
                      {isExpanded && (
                        <div className="ml-3 flex-1 flex items-center justify-between">
                          <span className="select-none text-[0.775rem] font-semibold text-white">{label}</span>
                          {!paidActive && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
                              <Lock size={10} />
                              Ücretli
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })()}

            {additionalApps.length > 0 && isExpanded && <div className="mx-3 my-2"></div>}
          </div>

          {isExpanded && fileUsage && canListFiles && (
            <div
              className="shrink-0 px-3 py-3 border-t border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
              onClick={() => navigate("/files")}>
              <div className="text-white text-xs mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">Depolama</span>
                  <span>
                    {formatFileSize(fileUsage.used)} / {formatFileSize(fileUsage.total)}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${fileUsage.percentage > 90 ? "bg-red-500" : fileUsage.percentage > 70 ? "bg-yellow-500" : "bg-blue-500"
                      }`}
                    style={{ width: `${Math.min(fileUsage.percentage, 100)}%` }}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {collapsedFlyout && !isExpanded && !isMobile && !isEditMode && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-[100] max-w-[min(16rem,calc(100vw-5rem))] truncate rounded-lg border border-white/15 bg-[#1a0f32] px-3 py-1.5 text-left text-[0.775rem] font-semibold leading-tight text-white shadow-xl"
          style={{
            top: collapsedFlyout.top,
            left: collapsedFlyout.left,
            transform: "translateY(-50%)",
          }}>
          {collapsedFlyout.label}
        </div>
      )}
    </>
  );
};

export default Sidebar;
